import type {
  PermissionList200ResourcesItem,
  RoleCreateBodyPermissionsItem,
  RoleList200RolesItemPermissions,
} from "@/api/generated/models"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

// API の操作追加時にラベル漏れを型エラーにする
export const ACTION_LABELS: Record<RoleCreateBodyPermissionsItem, string> = {
  read: "閲覧",
  create: "作成",
  update: "編集",
  delete: "削除",
}

export type Action = RoleCreateBodyPermissionsItem
export const ACTIONS = Object.keys(ACTION_LABELS) as Action[]

export type Draft = Record<string, Action[]>

/** 未知の権限を編集時に失わないよう、そのまま複製する。 */
export const toDraft = (permissions: RoleList200RolesItemPermissions | undefined): Draft =>
  Object.fromEntries(
    Object.entries(permissions ?? {}).map(([resource, actions]) => [resource, [...actions]])
  )

interface PermissionGridProps {
  resources: PermissionList200ResourcesItem[]
  draft: Draft
  onChange: (draft: Draft) => void
}

/** バックエンドで有効な権限だけを表示する。 */
export function PermissionGrid({ resources, draft, onChange }: PermissionGridProps) {
  const handleToggle = (resource: string, action: Action, checked: boolean) => {
    const currentActions = draft[resource] ?? []
    const nextActions = checked
      ? [...currentActions, action]
      : currentActions.filter((currentAction) => currentAction !== action)
    const { [resource]: _removedActions, ...otherPermissions } = draft
    onChange(
      nextActions.length > 0 ? { ...otherPermissions, [resource]: nextActions } : otherPermissions
    )
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-40 whitespace-nowrap">画面</TableHead>
            {ACTIONS.map((action) => (
              <TableHead key={action} className="w-20 text-center whitespace-nowrap">
                {ACTION_LABELS[action]}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {resources.map((resource) => (
            <TableRow key={resource.resource}>
              <TableCell>{resource.label}</TableCell>
              {ACTIONS.map((action) => (
                <TableCell key={action} className="text-center">
                  {resource.actions.includes(action) ? (
                    <div className="flex justify-center">
                      <Checkbox
                        checked={(draft[resource.resource] ?? []).includes(action)}
                        onChange={(event) =>
                          handleToggle(resource.resource, action, event.currentTarget.checked)
                        }
                      />
                    </div>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="mt-2 text-sm text-slate-500">
        — は現在その操作に権限チェックが無いことを表します
      </p>
    </div>
  )
}
